import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { getCleanPdfName } from '../handleDateAndPdf'
import { FaArrowLeft } from 'react-icons/fa'
// import { validateCin } from '../api/dashboardApi'

const AddInsight = ({
  insightId = null,
  fundingCompanyId = null,
  isEdit = false,
  insightType,
  apiConfig: { createFn, updateFn, getFn, companyGetFn },
}) => {
  const navigate = useNavigate()
  const [imagePreview, setImagePreview] = useState(null)
  const [pdfFile, setPdfFile] = useState(null)
  const [pdfFileOld, setPdfFileOld] = useState(null)
  const [pdfPreview, setPdfPreview] = useState(null)
  // const [cinValidation, setCinValidation] = useState({
  //   isValidating: false,
  //   isValid: null,
  //   companyName: '',
  //   error: '',
  // })

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      companyName: '',
      cin: '',
      fundingDate: '',
      fundingDetails: '',
      pdfPrice: '',
      pdfFile: null,
      companyLogo: null,
    },
  })

  // Updated React component validation function
  // const validateCIN = async (cinNumber) => {
  //   if (!cinNumber || cinNumber.length !== 21) {
  //     setCinValidation({
  //       isValidating: false,
  //       isValid: false,
  //       companyName: '',
  //       error: 'Invalid CIN format',
  //     })
  //     return
  //   }

  //   setCinValidation((prev) => ({ ...prev, isValidating: true }))

  //   try {
  //     setCinValidation((prev) => ({
  //       ...prev,
  //       isValidating: true,
  //     }))

  //     const res = await validateCin(cinNumber)
  //     console.log('Validation result:', res)

  //     if (res?.company) {
  //       setCinValidation({
  //         isValidating: false,
  //         isValid: true,
  //         companyName: res.company,
  //         error: '',
  //       })
  //     } else {
  //       setCinValidation({
  //         isValidating: false,
  //         isValid: false,
  //         companyName: '',
  //         error: res?.error || 'CIN not found',
  //       })
  //     }
  //   } catch (error) {
  //     console.error('CIN validation error:', error)
  //     setCinValidation({
  //       isValidating: false,
  //       isValid: false,
  //       companyName: '',
  //       error: error?.response?.data?.error || 'Error validating CIN',
  //     })
  //   }
  // }

  // 3. Add this useEffect to watch for CIN changes
  // useEffect(() => {
  //   const cinValue = watch('cin')
  //   if (cinValue && cinValue.length === 21) {
  //     const debounceTimer = setTimeout(() => {
  //       validateCIN(cinValue)
  //     }, 500)

  //     return () => clearTimeout(debounceTimer)
  //   } else if (cinValue && cinValue.length > 0) {
  //     setCinValidation({
  //       isValidating: false,
  //       isValid: null,
  //       companyName: '',
  //       error: '',
  //     })
  //   }
  // }, [watch('cin')])

  const {
    data: insightData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['insights', insightId],
    queryFn: () => getFn(insightId),
    enabled: isEdit && !!insightId,
  })

  const populateForm = (data) => {
    if (!data) {
      toast.error('No data available to populate form')
      return
    }

    if (data.companyName) {
      setValue('companyName', data.companyName || '')
    }

    if (data.cin) {
      setValue('cin', data.cin || '')
    }

    setValue('pdfPrice', data.pdfPrice || '')
    setValue('fundingDetails', data.fundingDetails || '')
    setValue('fundingDate', data.fundingDate || '')

    if (data.pdfName) {
      const cleanName = getCleanPdfName(data.pdfName)
      setPdfFileOld(cleanName)
      setPdfPreview(data.pdfName)
    }

    if (data.companyLogoUrl) {
      setImagePreview(data.companyLogoUrl)
    }
  }

  // Handle query success/error with useEffect instead of deprecated callbacks
  useEffect(() => {
    if (isEdit && insightData) {
      populateForm(insightData)
    }
  }, [isEdit, insightData])

  const { data: companyData } = useQuery({
    queryKey: ['companyData', fundingCompanyId],
    queryFn: () => companyGetFn(fundingCompanyId),
    enabled: !!fundingCompanyId && (!isEdit || insightType === 'funding365'),
  })

  const populateCompany = (data) => {
    if (!data) {
      toast.error('No data available to populate form')
      return
    }

    setValue('companyName', data.companyName || '')
    setValue('cin', data.cin || '')

    if (data.companyLogoUrl) {
      setImagePreview(data.companyLogoUrl)
    }
  }

  // Handle query success/error with useEffect instead of deprecated callbacks
  useEffect(() => {
    if (fundingCompanyId && companyData) {
      populateCompany(companyData)
    }
  }, [fundingCompanyId, companyData])

  useEffect(() => {
    if (error) {
      toast.error('Failed to load data')
      insightType === 'funding365'
        ? navigate(`/dealdashboard/funding`)
        : navigate(`/dealdashboard/${insightType}`)
    }
  }, [error, navigate, insightType])

  useEffect(() => {
    const imageFile = watch('companyLogo')
    const pdfFileFromForm = watch('pdfFile')

    // Handle image preview
    if (imageFile && imageFile[0]) {
      const previewUrl = URL.createObjectURL(imageFile[0])
      setImagePreview(previewUrl)
      return () => URL.revokeObjectURL(previewUrl)
    }

    // Handle PDF preview
    if (pdfFileFromForm && pdfFileFromForm[0]) {
      const previewUrl = URL.createObjectURL(pdfFileFromForm[0])
      setPdfPreview(previewUrl)
      setPdfFile(pdfFileFromForm[0])
      return () => URL.revokeObjectURL(previewUrl)
    }
  }, [watch('companyLogo'), watch('pdfFile')])

  // Create deal mutation
  const createMutation = useMutation({
    mutationFn: (data) => createFn(data),
    onSuccess: () => {
      toast.success(`${insightType} added successfully!`)
      reset()
      setImagePreview(null)
      setPdfPreview(null)
      setPdfFile(null)
      insightType === 'funding365'
        ? navigate(`/dealdashboard/funding`)
        : navigate(`/dealdashboard/${insightType}`)
    },
    onError: (error) => {
      console.error('Error creating insight:', error)
      toast.error(
        error.response?.data?.message ||
          'Error creating insight. Please try again.'
      )
    },
  })

  // Update deal mutation
  const updateMutation = useMutation({
    mutationFn: (data) => updateFn(insightId, data),
    onSuccess: () => {
      toast.success(`${insightType} updated successfully!`)
      insightType === 'funding365'
        ? navigate(`/dealdashboard/funding`)
        : navigate(`/dealdashboard/${insightType}`)
    },
    onError: (error) => {
      console.error('Error details:', error)
      toast.error(error.response?.data?.message || 'Failed to update insight')
    },
  })

  const handleImageChange = (e) => {
    const file = e.target.files
    if (file && file.length > 0) {
      setValue('companyLogo', file)
    }
  }

  const handlePdfChange = (e) => {
    const file = e.target.files
    if (file && file.length > 0) {
      setPdfFile(file)
      setValue('pdfFile', file)
      // setPdfPreview(file)
    }
  }

  const onSubmit = async (data) => {
    console.log('Form submitted with data:', data)

    // For create mode, require at least one image and PDF

    // if (!fundingCompanyId || !isEdit) {
    //   if (!data.companyLogo || !data.companyLogo[0]) {
    //     toast.error('Please upload a company logo')
    //     return
    //   }
    // }

    // if (!cinValidation.isValid) {
    //   setError('cin', {
    //     type: 'manual',
    //     message: 'Please enter a correct CIN number',
    //   })
    //   toast.error('Please enter a correct CIN number')
    //   return
    // }

    if (!isEdit) {
      if (!data.pdfFile || !data.pdfFile[0]) {
        toast.error('Please upload a PDF file')
        return
      }
    }

    if (isEdit && insightId) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  if (isEdit && isLoading) {
    return (
      <div className="max-w-8xl text-center mx-auto px-4 py-6">
        <p className="text-gray-600">{`Loading ${insightType} data...`}</p>
      </div>
    )
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="max-w-8xl text-left mx-auto px-4 py-6">
          <div className="grid md:grid-cols-2 gap-6 mb-4">
            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">Company Name</h2>
              <input
                type="text"
                readOnly={insightType === 'funding365' && !!fundingCompanyId}
                placeholder="Enter company name"
                {...register('companyName', {
                  required: !fundingCompanyId && 'Company Name is required',
                })}
                className={`border rounded-lg p-3 w-full text-base focus:outline-none ${
                  errors.companyName
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'
                } ${fundingCompanyId ? 'bg-gray-300' : ''}`}
              />
              {errors.companyName && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.companyName.message}
                </p>
              )}
            </div>

            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">
                Company CIN Number
              </h2>
              <input
                type="text"
                readOnly={insightType === 'funding365' && !!fundingCompanyId}
                placeholder="Enter CIN number"
                {...register('cin', {
                  required: !fundingCompanyId && 'CIN number is required',
                  pattern: {
                    value: /^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/,
                    message: 'Please enter a valid CIN number format',
                  },
                })}
                className={`border rounded-lg p-3 w-full text-base focus:outline-none ${
                  errors.cin
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'
                } ${fundingCompanyId ? 'bg-gray-300' : ''}`}
              />
              {errors.cin && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.cin.message}
                </p>
              )}
              {/* CIN Validation Status */}
              {/* {cinValidation.isValidating && (
                <div className="flex items-center mt-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                  <span className="text-sm">Validating CIN...</span>
                </div>
              )}

              {cinValidation.isValid === true && (
                <div className="flex items-center mt-2 text-green-600">
                  <svg
                    className="h-4 w-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-sm font-medium">
                    {cinValidation.companyName}
                  </span>
                </div>
              )}

              {cinValidation.isValid === false && (
                <div className="flex items-center mt-2 text-red-600">
                  <svg
                    className="h-4 w-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span className="text-sm">{cinValidation.error}</span>
                </div>
              )} */}
            </div>

            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">
                {insightType === 'latestDeal'
                  ? 'Date Of Funding'
                  : 'Financial Year'}
              </h2>
              <input
                type={insightType === 'latestDeal' ? 'date' : 'text'}
                placeholder={
                  insightType === 'latestDeal'
                    ? 'Format like 03 Mar 2025'
                    : 'Format like 2025-26'
                }
                {...register('fundingDate', {
                  required: 'Funding date is required',
                })}
                className={`border rounded-lg p-3 w-full text-base focus:outline-none ${
                  errors.fundingDate
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'
                }`}
              />
              {errors.fundingDate && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.fundingDate.message}
                </p>
              )}
            </div>

            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">
                Selling Price
              </h2>
              <input
                type="number"
                placeholder="Enter amount"
                {...register('pdfPrice', {
                  required: 'price is required',
                  min: {
                    value: 1,
                    message: 'Amount must be greater than 0',
                  },
                })}
                className={`border rounded-lg p-3 w-full text-base focus:outline-none ${
                  errors.pdfPrice
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'
                }`}
              />
              {errors.pdfPrice && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.pdfPrice.message}
                </p>
              )}
            </div>

            <div className="bg-white p-6 rounded-xl shadow col-span-2">
              <h2 className="font-semibold text-gray-800 mb-2">
                Details Of Funding
              </h2>
              <textarea
                rows="5"
                placeholder="Write the content here..."
                {...register('fundingDetails', {
                  required: 'Content is required',
                })}
                className={`border rounded-lg p-3 w-full text-base focus:outline-none ${
                  errors.fundingDetails
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-orange-500 focus:ring-orange-500'
                }`}
              />
              {errors.fundingDetails && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.fundingDetails.message}
                </p>
              )}
            </div>

            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">
                Upload PDF {!isEdit && <span className="text-red-500">*</span>}
              </h2>
              {(pdfFileOld || pdfFile) && (
                <div className="mt-4">
                  <p className="text-sm text-gray-700 mb-2">
                    File selected: {pdfFile ? pdfFile.name : pdfFileOld}
                  </p>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <object
                      data={pdfPreview}
                      type="application/pdf"
                      className="w-full h-64"
                    >
                      <div className="p-4 bg-gray-50 text-center">
                        <p className="text-gray-600">
                          PDF preview not available
                        </p>
                        <a
                          href={pdfPreview}
                          target="_blank"
                          rel="noreferrer"
                          className="text-orange-500 hover:text-orange-600 font-medium mt-2 inline-block"
                        >
                          Open PDF in new tab
                        </a>
                      </div>
                    </object>
                  </div>
                </div>
              )}
              <input
                type="file"
                accept=".pdf"
                onChange={handlePdfChange}
                {...register('pdfFile', {
                  required: isEdit ? false : 'pdfFile is required',
                  // validate: {
                  //   fileSize: (files) => {
                  //     if (
                  //       files &&
                  //       files[0] &&
                  //       files[0].size > 5 * 1024 * 1024
                  //     ) {
                  //       return 'File size must be less than 2MB'
                  //     }
                  //     return true
                  //   },
                  // },
                })}
                className="mt-3 block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-600 hover:file:bg-orange-100"
              />
              {errors.pdfFile && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.pdfFile.message}
                </p>
              )}
            </div>

            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="font-semibold text-gray-800 mb-2">
                Company Logo{' '}
                {!isEdit && <span className="text-red-500">*</span>}
              </h2>
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="mt-4 max-h-64 object-cover rounded-lg border border-gray-200"
                />
              )}
              {!fundingCompanyId && (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    {...register('companyLogo', {
                      required: isEdit ? false : 'companyLogo is required',
                      // validate: {
                      //   fileSize: (files) => {
                      //     if (
                      //       files &&
                      //       files[0] &&
                      //       files[0].size > 2 * 1024 * 1024
                      //     ) {
                      //       return 'File size must be less than 2MB'
                      //     }
                      //     return true
                      //   },
                      // },
                    })}
                    className="mt-3 block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-600 hover:file:bg-orange-100"
                  />
                  {errors.companyLogo && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.companyLogo.message}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-between items-center mt-8 text-center">
            {/* <Link
              to={
                !!fundingCompanyId
                  ? `/dealdashboard/funding/report/${fundingCompanyId}`
                  : '/dealdashboard/funding'
              }
            >
              Back
            </Link> */}

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex justify-center items-center bg-gray-500 p-2 text-white rounded"
            >
              <FaArrowLeft />
              &nbsp; Back
            </button>

            <button
              type="submit"
              disabled={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
              className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white text-lg font-medium px-8 py-3 rounded-lg shadow-md transition-all"
            >
              {isSubmitting ||
              createMutation.isPending ||
              updateMutation.isPending
                ? 'Processing...'
                : isEdit
                ? 'Update Insight'
                : 'Add Insight'}
            </button>
          </div>
        </div>
      </form>
    </>
  )
}

export default AddInsight
